import { createRoute, useLoaderData } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import type { MediaLibraryPageInitialData } from "~/app/api/pageData/mediaLibraryPageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import { createMediaLibrarySdsPage } from "~/features/mediaLibrarySds/MediaLibrarySdsPage";
import type { MediaFilterData, OptionItem } from "~/features/mediaLibrary/types/mediaLibraryFormTypes";
import { mediaLibraryPageSdsQueryOptions } from "~/app/api/pageData/mediaLibrarySdsPageQuery";

function toOptions(entries: Record<string, string> | undefined): OptionItem[] {
  if (!entries) {
    return [];
  }
  return Object.entries(entries).map(([key, label]) => ({ key, label }));
}

function toMediaFilterData(data: MediaLibraryPageInitialData | undefined): MediaFilterData {
  return {
    mediaTypes: toOptions(data?.mediaTypes),
    productPortfolios: toOptions(data?.productPortfolios),
    languages: toOptions(data?.mediaLanguages),
    segments: toOptions(data?.segments),
    country: toOptions(data?.country),
  };
}

function MediaLibrarySdsRoute() {
  const result = useLoaderData({
    from: "/sds-media-library",
  }) as PageInitialData<MediaLibraryPageInitialData>;
  const shellProps = useStorefrontShellProps();
  const filterData = toMediaFilterData(result.data);

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {createMediaLibrarySdsPage(filterData)}
    </StorefrontShellFrame>
  );
}

export async function loadMediaLibrarySdsPageData(context: { queryClient: QueryClient }) {
  const result = await context.queryClient.ensureQueryData(mediaLibraryPageSdsQueryOptions());

  return unwrapPageInitialData(result);
}

export const mediaLibrarySdsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sds-media-library",
  loader: async ({ context }) => loadMediaLibrarySdsPageData(context),
  component: MediaLibrarySdsRoute,
});
